import { useState, useEffect } from 'react';
import axios from 'axios';
import YouTubeGoogleConnect from './YouTubeGoogleConnect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SocialConnection = () => {
  const [socialAccounts, setSocialAccounts] = useState({});
  const [showTokenForm, setShowTokenForm] = useState(null);
  const [tokenForm, setTokenForm] = useState({
    platform: '',
    access_token: '',
    account_id: '',
    account_name: '',
    refresh_token: '',
    client_id: '',
    client_secret: ''
  });
  const [quickToken, setQuickToken] = useState(''); // Estado separado para conexão rápida
  const [loading, setLoading] = useState(false);
  const [deletingPlatform, setDeletingPlatform] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [userLimits, setUserLimits] = useState(null);

  useEffect(() => {
    fetchSocialAccounts();
    fetchUserLimits();
    // Retorno do fluxo "Conectar com Google"
    const params = new URLSearchParams(window.location.search);
    if (params.get('youtube') === 'connected') {
      setMessage(`YouTube conectado com sucesso${params.get('channel') ? `: ${params.get('channel')}` : ''}! Renovação automática ativada.`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('youtube_error')) {
      setError(`Falha ao conectar YouTube: ${params.get('youtube_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchUserLimits = async () => {
    try {
      const response = await axios.get(`${API}/user/limits`);
      setUserLimits(response.data);
    } catch (err) {
      console.error('Error fetching limits:', err);
      setUserLimits({
        allowed_social: ['instagram', 'facebook'],
        is_unlimited: false,
        is_admin: false,
        plan: 'free_trial'
      });
    }
  };

  const isSocialAllowed = (socialKey) => {
    if (!userLimits) return false;
    if (userLimits.is_unlimited || userLimits.is_admin) return true;
    if (userLimits.allowed_social === 'all') return true;
    return userLimits.allowed_social.includes(socialKey.toLowerCase());
  };

  const fetchSocialAccounts = async () => {
    try {
      const response = await axios.get(`${API}/social/accounts`);
      setSocialAccounts(response.data.social_accounts);
    } catch (error) {
      console.error('Failed to fetch social accounts:', error);
    }
  };

  const openConnectForm = (platform) => {
    setTokenForm({
      platform: platform,
      access_token: '',
      account_id: '',
      account_name: '',
      refresh_token: '',
      client_id: '',
      client_secret: ''
    });
    setShowTokenForm(platform);
    setMessage('');
    setError('');
  };

  const connectAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        platform: tokenForm.platform,
        access_token: tokenForm.access_token,
        account_id: tokenForm.account_id,
        account_name: tokenForm.account_name
      };
      // Enviar credenciais OAuth apenas para YouTube se preenchidas
      if (tokenForm.platform === 'youtube') {
        if (tokenForm.refresh_token) payload.refresh_token = tokenForm.refresh_token;
        if (tokenForm.client_id) payload.client_id = tokenForm.client_id;
        if (tokenForm.client_secret) payload.client_secret = tokenForm.client_secret;
      }
      const response = await axios.post(`${API}/social/connect`, payload);

      setMessage(`${tokenForm.platform.toUpperCase()} conectado com sucesso!`);
      setShowTokenForm(null);
      setTokenForm({ platform: '', access_token: '', account_id: '', account_name: '', refresh_token: '', client_id: '', client_secret: '' });
      
      // Refresh accounts list
      fetchSocialAccounts();
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao conectar conta');
    } finally {
      setLoading(false);
    }
  };

  const testPost = async (platform) => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const testContent = `🎉 Teste de postagem automática via AffiliateAI! 

Sistema funcionando perfeitamente para automação de marketing de afiliados.

#affiliatemarketing #automation #test`;

      const response = await axios.post(`${API}/social/post`, {
        platform: platform,
        content: testContent
      });

      setMessage(`Post de teste criado com sucesso no ${platform.toUpperCase()}!`);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao criar post de teste');
    } finally {
      setLoading(false);
    }
  };

  const disconnectAccount = async (platform) => {
    if (!window.confirm(`Tem certeza que deseja desconectar ${platform.toUpperCase()}?`)) {
      return;
    }
    
    setDeletingPlatform(platform);
    setError('');
    setMessage('');

    try {
      await axios.delete(`${API}/social/disconnect/${platform}`);
      setMessage(`${platform.toUpperCase()} desconectado com sucesso!`);
      fetchSocialAccounts();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao desconectar conta');
    } finally {
      setDeletingPlatform(null);
    }
  };

  const platforms = [
    {
      name: 'Instagram',
      key: 'instagram',
      icon: '📸',
      color: 'pink',
      description: 'Conecte sua conta Business do Instagram para postagem automática',
      instructions: [
        '1. Acesse Facebook Developers Console',
        '2. Crie um App com Instagram Basic Display',
        '3. Configure Instagram Business Account',
        '4. Gere um token de longa duração',
        '5. Cole o token abaixo'
      ]
    },
    {
      name: 'Facebook',
      key: 'facebook', 
      icon: '👥',
      color: 'blue',
      description: 'Conecte sua página do Facebook para posts automáticos',
      instructions: [
        '1. Use o mesmo token do Instagram',
        '2. Certifique-se de ter uma página comercial',
        '3. Token deve ter permissões de publicação',
        '4. Cole o token abaixo'
      ]
    },
    {
      name: 'TikTok',
      key: 'tiktok',
      icon: '🎵',
      color: 'purple',
      description: 'Conecte sua conta TikTok Business para vídeos automáticos',
      instructions: [
        '1. Acesse TikTok Developer Portal (developers.tiktok.com)',
        '2. Crie um App e registre sua aplicação',
        '3. Configure TikTok for Business API',
        '4. Obtenha Client Key e Client Secret',
        '5. Gere Access Token com permissão video.publish',
        '6. Cole o Access Token abaixo'
      ]
    },
    {
      name: 'YouTube',
      key: 'youtube',
      icon: '📺',
      color: 'red',
      description: 'Conecte seu canal YouTube para upload automático de vídeos',
      instructions: [
        '1. Acesse Google Cloud Console (console.cloud.google.com)',
        '2. Crie um novo projeto',
        '3. Ative YouTube Data API v3',
        '4. Crie credenciais OAuth 2.0',
        '5. Configure tela de consentimento',
        '6. Gere Access Token com escopo youtube.upload',
        '7. Cole o Access Token abaixo',
        '⚠️ IMPORTANTE: Coloque também o ID do Canal (ex: UCxxxxxxx)'
      ]
    },
    {
      name: 'Twitter/X',
      key: 'twitter',
      icon: '🐦',
      color: 'sky',
      description: 'Conecte sua conta do Twitter/X para tweets automáticos',
      instructions: [
        '1. Acesse developer.twitter.com',
        '2. Crie um App de desenvolvedor',
        '3. Gere as chaves API e tokens',
        '4. Cole o Access Token abaixo'
      ]
    },
    {
      name: 'LinkedIn',
      key: 'linkedin',
      icon: '💼',
      color: 'blue',
      description: 'Conecte seu LinkedIn para posts profissionais',
      instructions: [
        '1. Acesse LinkedIn Developer Portal',
        '2. Crie um App',
        '3. Solicite permissões de posting',
        '4. Gere Access Token',
        '5. Cole o token abaixo'
      ]
    },
    {
      name: 'Pinterest',
      key: 'pinterest',
      icon: '📌',
      color: 'red',
      description: 'Conecte seu Pinterest para pins automáticos',
      instructions: [
        '1. Acesse developers.pinterest.com',
        '2. Crie um App',
        '3. Gere Access Token',
        '4. Cole o token abaixo'
      ]
    },
    {
      name: 'WhatsApp Business',
      key: 'whatsapp',
      icon: '💬',
      color: 'green',
      description: 'Conecte WhatsApp Business API para mensagens',
      instructions: [
        '1. Acesse Meta Business Suite',
        '2. Configure WhatsApp Business API',
        '3. Gere o token de acesso',
        '4. Cole o token abaixo'
      ]
    },
    {
      name: 'Telegram',
      key: 'telegram',
      icon: '✈️',
      color: 'blue',
      description: 'Conecte seu Bot Telegram para mensagens automáticas',
      instructions: [
        '1. Fale com @BotFather no Telegram',
        '2. Crie um novo bot com /newbot',
        '3. Copie o token gerado',
        '4. Cole o token abaixo'
      ]
    }
  ];

  const isTrialPlan = userLimits?.plan === 'free_trial';
  const isSocialLocked = (socialKey) => !isSocialAllowed(socialKey);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🔗 Conectar Redes Sociais</h1>
        <p className="text-gray-600">Configure suas contas para postagem automática de marketing de afiliados</p>
      </div>

      {/* Aviso de limitação do plano */}
      {isTrialPlan && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 font-semibold mb-2">⚠️ Plano Teste Grátis (7 dias)</p>
          <p className="text-sm text-yellow-700">
            No período de teste você tem acesso a <strong>2 redes sociais</strong>: Instagram e Facebook.
            <br />
            <a href="/upgrade" className="text-blue-600 hover:underline font-medium">
              Faça upgrade para liberar todas as 10 redes sociais! →
            </a>
          </p>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2">📱 O que são Conexões Sociais?</h3>
          <p className="text-sm text-blue-800">
            Conecte suas redes sociais uma vez e o sistema poderá postar automaticamente o conteúdo gerado pela IA + seus links de afiliado.
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-bold text-green-900 mb-2">🎯 Por que conectar?</h3>
          <p className="text-sm text-green-800">
            Economize tempo! Gere conteúdo uma vez e publique em Instagram, Facebook, TikTok e YouTube automaticamente com seus links.
          </p>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {message}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Quick Token Input */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-blue-900 mb-4">
          🚀 Conexão Rápida - Instagram/Facebook
        </h2>
        <p className="text-blue-800 mb-4">
          Você já tem um token? Cole aqui para conectar Instagram e Facebook simultaneamente:
        </p>
        
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Cole seu token do Instagram/Facebook aqui..."
            className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={quickToken}
            onChange={(e) => setQuickToken(e.target.value)}
          />
          <button
            onClick={async () => {
              if (quickToken) {
                setLoading(true);
                setError('');
                setMessage('');
                try {
                  await axios.post(`${API}/social/connect`, {
                    platform: 'instagram',
                    access_token: quickToken,
                    account_id: '',
                    account_name: ''
                  });
                  setMessage('Instagram/Facebook conectado com sucesso!');
                  setQuickToken('');
                  fetchSocialAccounts();
                } catch (err) {
                  setError(err.response?.data?.detail || 'Erro ao conectar conta');
                } finally {
                  setLoading(false);
                }
              }
            }}
            disabled={!quickToken || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg disabled:opacity-50"
          >
            {loading ? 'Conectando...' : 'Conectar Agora'}
          </button>
        </div>
        
        <div className="mt-3 text-sm text-blue-700">
          💡 <strong>Dica:</strong> Obtenha seu token no Facebook Developer Console com permissões de Instagram Business
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {platforms.map(platform => {
          const isConnected = socialAccounts[platform.key];
          const isLocked = isSocialLocked(platform.key);
          
          return (
            <div key={platform.key} className={`bg-white rounded-lg shadow-lg p-6 ${isLocked ? 'opacity-60 relative' : ''}`}>
              {/* Lock Overlay */}
              {isLocked && (
                <div className="absolute inset-0 bg-gray-100/80 rounded-lg flex items-center justify-center z-10">
                  <div className="text-center p-4">
                    <span className="text-4xl">🔒</span>
                    <p className="text-gray-600 font-medium mt-2">Bloqueado no Teste Grátis</p>
                    <a href="/upgrade" className="text-sm text-blue-600 hover:underline">Fazer upgrade →</a>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{platform.icon}</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{platform.name}</h3>
                    <p className="text-sm text-gray-600">{platform.description}</p>
                  </div>
                </div>
                
                {isConnected ? (
                  <div className="flex flex-col items-end space-y-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✅ Conectado
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => testPost(platform.key)}
                        disabled={loading || isLocked}
                        className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded disabled:opacity-50"
                      >
                        Testar Post
                      </button>
                      {platform.key === 'youtube' && (
                        <button
                          onClick={async () => {
                            setLoading(true);
                            setError(''); setMessage('');
                            try {
                              const r = await axios.post(`${API}/social/youtube/refresh`);
                              setMessage(r.data.message || 'Token renovado!');
                              fetchSocialAccounts();
                            } catch (err) {
                              setError(err.response?.data?.detail || 'Erro ao renovar token');
                            } finally { setLoading(false); }
                          }}
                          disabled={loading}
                          className="text-sm bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded disabled:opacity-50"
                          data-testid="youtube-refresh-btn"
                        >
                          🔄 Renovar Token
                        </button>
                      )}
                      {platform.key === 'youtube' && (
                        <button
                          onClick={() => openConnectForm('youtube')}
                          className="text-sm bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1 rounded border border-red-200"
                          data-testid="youtube-reconnect-google-btn"
                        >
                          Reconectar com Google
                        </button>
                      )}
                      <button
                        onClick={() => disconnectAccount(platform.key)}
                        disabled={deletingPlatform === platform.key}
                        className="text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded disabled:opacity-50"
                      >
                        {deletingPlatform === platform.key ? '...' : '🗑️ Excluir'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openConnectForm(platform.key)}
                    data-testid={`connect-${platform.key}-btn`}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Conectar
                  </button>
                )}
              </div>

              {isConnected && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <div className="text-sm text-green-800">
                    <p><strong>Conta:</strong> {isConnected.account_name || 'Conectada'}</p>
                    <p><strong>ID:</strong> {isConnected.account_id}</p>
                    <p><strong>Conectado em:</strong> {new Date(isConnected.connected_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
              )}

              {!isConnected && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Como obter o token:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {platform.instructions.map((instruction, index) => (
                      <li key={index}>{instruction}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Token Form Modal */}
      {showTokenForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Conectar {tokenForm.platform.charAt(0).toUpperCase() + tokenForm.platform.slice(1)}
              </h3>
              
              <form onSubmit={connectAccount} className="space-y-4">
                {tokenForm.platform === 'youtube' && (
                  <YouTubeGoogleConnect
                    clientId={tokenForm.client_id}
                    clientSecret={tokenForm.client_secret}
                    onChange={(field, value) => setTokenForm({ ...tokenForm, [field]: value })}
                    onError={setError}
                  />
                )}

                {tokenForm.platform === 'youtube' && (
                  <details className="border-t border-gray-200 pt-3">
                    <summary className="text-sm text-gray-600 cursor-pointer select-none" data-testid="youtube-manual-toggle">Ou conectar manualmente com token (avançado)</summary>
                <div className="space-y-4 mt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token de Acesso *
                  </label>
                  <textarea
                    required={tokenForm.platform !== 'youtube'}
                    rows={3}
                    value={tokenForm.access_token}
                    onChange={(e) => setTokenForm({...tokenForm, access_token: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Cole seu token de acesso aqui..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID da Conta (OBRIGATÓRIO para YouTube)
                  </label>
                  <input
                    type="text"
                    value={tokenForm.account_id}
                    onChange={(e) => setTokenForm({...tokenForm, account_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ID do Canal (ex: UCxxxxxxxx)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Token (Opcional)</label>
                  <textarea
                    rows={2}
                    value={tokenForm.refresh_token}
                    onChange={(e) => setTokenForm({...tokenForm, refresh_token: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs"
                    placeholder="1//0g... (Refresh Token do Google OAuth)"
                    data-testid="youtube-refresh-token-input"
                  />
                </div>
                </div>
                  </details>
                )}

                {tokenForm.platform !== 'youtube' && (<>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token de Acesso *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={tokenForm.access_token}
                    onChange={(e) => setTokenForm({...tokenForm, access_token: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Cole seu token de acesso aqui..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID da Conta (Opcional)
                  </label>
                  <input
                    type="text"
                    value={tokenForm.account_id}
                    onChange={(e) => setTokenForm({...tokenForm, account_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ID da conta (será detectado automaticamente se não fornecido)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Conta (Opcional)
                  </label>
                  <input
                    type="text"
                    value={tokenForm.account_name}
                    onChange={(e) => setTokenForm({...tokenForm, account_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nome da conta para identificação"
                  />
                </div>
                </>)}


                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Importante:</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• Use um token de longa duração (long-lived token)</li>
                    <li>• Certifique-se de que o token tem permissões de publicação</li>
                    <li>• Para Instagram, você precisa de uma conta Business ou Creator</li>
                    <li>• O token será validado antes de ser salvo</li>
                  </ul>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTokenForm(null);
                      setTokenForm({ platform: '', access_token: '', account_id: '', account_name: '' });
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !tokenForm.access_token}
                    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed ${tokenForm.platform === 'youtube' && !tokenForm.access_token ? 'hidden' : ''}`}
                    data-testid="connect-account-submit-btn"
                  >
                    {loading ? 'Conectando...' : tokenForm.platform === 'youtube' ? 'Conectar com Token' : 'Conectar Conta'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📚 Como Obter Tokens de Acesso
        </h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-800">Instagram/Facebook:</h3>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1 ml-2">
              <li>Acesse <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Facebook Developers</a></li>
              <li>Crie um novo app ou use um existente</li>
              <li>Adicione os produtos "Instagram Basic Display" e "Instagram API"</li>
              <li>Configure as permissões necessárias</li>
              <li>Gere um token de longa duração</li>
              <li>Cole o token no formulário acima</li>
            </ol>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">🎯 Token Pronto para Uso:</h4>
            <p className="text-sm text-blue-700 mb-2">
              Você já tem este token válido que pode usar imediatamente:
            </p>
            <code className="block text-xs bg-blue-100 p-2 rounded break-all">
              EAAPqRcn1rBcBPRcjdjZBvmqhGd3sptsyFNn47iSdMYfMZCpnj9uuZAu6ZB56edfJJzwt3xFK4sZBZC9cZCHfseBYk9C43VWZBOVbCjbLJ9QAAhm6Abm1Nr0Eg61xlZBsOSTFxfKZCta5sZBiRXJBOZBZAUXArGIzknUPT7LN6MYyPlmgnYYKWZAyDz8HYqQYlcbPfwuG2ouu3e3n1lMnPZA1ZANU8drT9nhaZBB3hdqCEbGAcFaXYHrQIAon3qOFFWjzyfV862ARopvlqddz4GfVP
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialConnection;